import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MoreVertical,
  Archive,
  ArchiveRestore,
  Trash2,
  CornerDownRight,
} from "lucide-react";
import { useCanDeleteTask } from "../../hooks/useCanDeleteTask";
import type { Task } from "../../types";

interface TaskCardMenuProps {
  /** The Task the menu acts on — it decides from this which items to offer. */
  task: Pick<Task, "archived" | "user_id" | "list_id">;
  onDelete: () => void;
  onArchive?: () => void;
  onConvertToSubtask?: () => void;
  onOpenChange?: (open: boolean) => void;
}

const TaskCardMenu: React.FC<TaskCardMenuProps> = ({
  task,
  onDelete,
  onArchive,
  onConvertToSubtask,
  onOpenChange,
}) => {
  const isArchived = !!task.archived;
  // Shared lists (#286): a Member may edit anybody's Task in a Shared list
  // but may only delete one they wrote themselves. Offering Delete anyway
  // would look like it worked — the database refuses it silently — and the
  // Task would come back on the next refetch.
  const canDelete = useCanDeleteTask(task);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpenState] = useState(false);

  const setOpen = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setOpenState((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        if (value !== prev) onOpenChange?.(value);
        return value;
      });
    },
    [onOpenChange],
  );
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!open) {
      setVisible(false);
      return;
    }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + 6, left: rect.left });
    }
  }, [open]);

  useEffect(() => {
    if (!open || !menuRef.current || !triggerRef.current) return;
    const menuWidth = menuRef.current.offsetWidth;
    const rect = triggerRef.current.getBoundingClientRect();
    let left = rect.left;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - 8 - menuWidth;
    }
    if (left < 8) left = 8;
    setPosition({ top: rect.bottom + 6, left });
    setVisible(true);
  }, [open]);

  // Move focus into the menu once it's positioned so keyboard users can
  // arrow through the items. On close, return focus to the trigger button
  // unless something else (e.g. the action that closed the menu) has
  // already moved focus away.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && visible) {
      const firstItem = menuRef.current?.querySelector<HTMLButtonElement>(
        '[role="menuitem"]',
      );
      firstItem?.focus();
      wasOpenRef.current = true;
    } else if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      if (
        triggerRef.current &&
        (document.activeElement === document.body ||
          document.activeElement === null)
      ) {
        triggerRef.current.focus();
      }
    }
  }, [open, visible]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, setOpen]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, setOpen]);

  const runAndClose = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    setOpen(false);
    action();
  };

  // Up/Down/Home/End move focus between menu items; stopPropagation keeps
  // the board's document-level arrow handler from firing on the same key.
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.key !== "ArrowDown" &&
      e.key !== "ArrowUp" &&
      e.key !== "Home" &&
      e.key !== "End"
    ) {
      return;
    }
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]',
      ) ?? [],
    );
    if (items.length === 0) return;
    const currentIndex = items.indexOf(
      document.activeElement as HTMLButtonElement,
    );
    let nextIndex = currentIndex;
    if (e.key === "ArrowDown") {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
    } else if (e.key === "ArrowUp") {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else {
      nextIndex = items.length - 1;
    }
    e.preventDefault();
    e.stopPropagation();
    items[nextIndex]?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="Task actions"
        aria-haspopup="menu"
        aria-expanded={open}
        className="text-warm-text-tertiary hover:text-warm-text-primary transition-colors duration-150 ease-in-out"
      >
        <MoreVertical size={14} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label="Task actions"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleMenuKeyDown}
            className="fixed z-50 bg-warm-bg-card border border-warm-border rounded-soft shadow-soft-md py-1 min-w-[160px] origin-top-left animate-popover-in"
            style={{
              top: position.top,
              left: position.left,
              visibility: visible ? "visible" : "hidden",
            }}
          >
            {onArchive && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => runAndClose(e, onArchive)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-warm-text-primary hover:bg-warm-bg-elevated transition-colors duration-150 ease-in-out"
              >
                {isArchived ? (
                  <>
                    <ArchiveRestore size={14} />
                    <span>Restore</span>
                  </>
                ) : (
                  <>
                    <Archive size={14} />
                    <span>Archive</span>
                  </>
                )}
              </button>
            )}
            {onConvertToSubtask && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => runAndClose(e, onConvertToSubtask)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-warm-text-primary hover:bg-warm-bg-elevated transition-colors duration-150 ease-in-out"
              >
                <CornerDownRight size={14} />
                <span>Convert to sub-task</span>
              </button>
            )}
            {canDelete && (onArchive || onConvertToSubtask) && (
              <div className="my-1 h-px bg-warm-border" aria-hidden="true" />
            )}
            {canDelete && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => runAndClose(e, onDelete)}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 dark:text-red-400 hover:bg-warm-bg-elevated transition-colors duration-150 ease-in-out"
              >
                <Trash2 size={14} />
                <span>Delete</span>
              </button>
            )}
          </div>,
          document.body,
        )}
    </>
  );
};

export default TaskCardMenu;
